import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion, AnimatePresence } from 'framer-motion';
import { FallbackUI } from './FallbackUI';

export const PortfolioSection: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();
  const [activeCategory, setActiveCategory] = useState(0);

  const categories = t.portfolio.categories;

  if (!categories || categories.length === 0) {
    return <FallbackUI message="Portfolio loading..." />;
  }

  // Generate placeholder items for each category
  const generateItems = (cat: string) => {
    return Array.from({ length: 6 }, (_, i) => ({
      id: `${cat}-${i}`,
      title: `${cat} ${i + 1}`,
      gradient: `hsl(${200 + i * 15}, 60%, ${8 + i * 2}%)`,
    }));
  };

  const activeItems = generateItems(categories[activeCategory]);

  return (
    <section id="portfolio" className="relative py-24 md:py-32 bg-vilarias-black">
      <div className="absolute inset-0 bg-hero-glow opacity-30" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.portfolio.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto mb-8" />
        </motion.div>

        {/* Category tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {categories.map((cat, index) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(index)}
              className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                activeCategory === index
                  ? 'bg-vilarias-accent text-white btn-glow'
                  : 'bg-vilarias-card text-vilarias-text-secondary hover:text-white border border-vilarias-border'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Gallery grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto"
          >
            {activeItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer"
              >
                <div 
                  className="absolute inset-0 transition-transform duration-700 group-hover:scale-110"
                  style={{ background: `linear-gradient(135deg, ${item.gradient}, #050505)` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-vilarias-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-12 h-12 rounded-full bg-vilarias-accent/20 border border-vilarias-accent/50 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-vilarias-accent" />
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-sm font-medium">{item.title}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
